import { component, OnInit, prop } from '@gbds/core/src/ts/core'
import { debounce } from '@gbds/core/src/ts/utils/debounce'
import { isMediumViewport, isSmallViewport } from '@gbds/core/src/ts/utils/viewport'
import { GBModalService } from '../../../utils/modal-service'
import { watchAndLoadElementImages } from '../../../utils/lazy-loader-utils'
import { applyDeepLinkFromUrl, syncUrlFromSelection, TargetZoneChangeDetail } from './target-zone-deep-link'

type TargetZone = HTMLElement & {
	dataZoneSectionId: string
	dataIsDefaultZone: string
	dataOpenOnSm: string
	dataOpenOnMd: string
	dataOpenOnLg: string
	dataTimeTo: number
	dataTimeFrom: number
	dataTarget: string
}

type Button = HTMLElement & {
	linkType: string
	targetZoneId: string
	targetZoneSectionId: string
}

@component('gb-target-zone')
export class GBTargetZone extends HTMLElement implements OnInit {
	/**
	 * Is this in edit mode?
	 */
	@prop()
	public dataIsEdit: string

	/**
	 * Zone ID
	 */
	@prop()
	public dataZoneId: string

	/**
	 * Use With
	 */
	@prop()
	public dataUseWith: string

	/**
	 * Start Time
	 */
	@prop()
	public dataTimeFrom: number

	/**
	 * End Time
	 */
	@prop()
	public dataTimeTo: number

	/**
	 * Is default zone
	 */
	@prop()
	public dataIsDefaultZone: boolean

	/**
	 * Enable Deep Link
	 */
	@prop('boolean')
	public dataEnableDeepLink: boolean

	private readonly onPopState = () => {
		if (this.dataEnableDeepLink) applyDeepLinkFromUrl(this)
	}
	private readonly onPageShow = (e: Event & { persisted?: boolean }) => {
		if (e.persisted && this.dataEnableDeepLink) applyDeepLinkFromUrl(this)
	}
	private deepLinkNavBound = false

	constructor() {
		super()
	}

	disconnectedCallback(): void {
		if (this.deepLinkNavBound) {
			window.removeEventListener('popstate', this.onPopState)
			window.removeEventListener('pageshow', this.onPageShow)
			this.deepLinkNavBound = false
		}
	}

	onInit(): void {
		if (this.dataIsEdit !== 'true') {
			if (this.dataUseWith === 'scrubber') {
				this.listenForTime()
			}
			if (this.dataUseWith === 'button') {
				this.listenForTargetZoneChange()
			}
		}

		// Watch for when this element comes into view and then load all its images, even if hidden
		watchAndLoadElementImages(this)
	}

	isHostedInShadowDom() {
		return this.getRootNode() instanceof ShadowRoot
	}

	/**
	 * Zone sections that belong to this target zone only (excludes nested gb-target-zone sections).
	 */
	private getOwnZoneSections(): TargetZone[] {
		return Array.from(this.querySelectorAll<TargetZone>('.gb-zone-section')).filter(
			section => section.closest('gb-target-zone') === this
		)
	}

	listenForTargetZoneChange(): void {
		const TZC = this.getOwnZoneSections()
		let Buttons = document.querySelectorAll<Button>('.gb-target-zone-button')
		if (this.isHostedInShadowDom) {
			const rootNode = this.getRootNode()
			Buttons = (<Element>rootNode).querySelectorAll<Button>('.gb-target-zone-button')
		}

		let isSmViewport = isSmallViewport()
		let isMdViewport = isMediumViewport()
		if (TZC.length > 0) {
			// If we have multiple zones, we'll want to use them independently
			let isModal = false
			for (let i = 0; i < TZC.length; i++) {
				isModal = false
				if (
					(isSmViewport && TZC[i].dataset.openOnSm === 'inModal') ||
					(isMdViewport && TZC[i].dataset.openOnMd === 'inModal') ||
					(!isSmViewport && !isMdViewport && TZC[i].dataset.openOnLg === 'inModal')
				) {
					isModal = true
				}
				if (!isModal) {
					if (TZC[i].dataset.isDefaultZone === 'true') {
						for (let j = 0; j < Buttons.length; j++) {
							if (
								Buttons[j].dataset.targetZoneSectionId === TZC[i].dataset.zoneSectionId &&
								Buttons[j].dataset.targetZoneId === this.dataZoneId
							) {
								Buttons[j].classList.add('gb-target-zone-button-active')
							}
						}
					}
				} else {
					TZC[i].classList.add('gb-hide')
				}
				if (TZC[i].dataset.isDefaultZone === 'true' && !isModal) {
					TZC[i].classList.remove('gb-hide')
				}
			}
		} else {
			// If we don't have multiple zones, then we'll use the parent
			if (this.dataIsDefaultZone) {
				this.classList.remove('gb-hide')
			} else {
				if (Buttons[0].dataset.targetZoneSectionId === this.dataZoneId) {
					Buttons[0].classList.add('gb-target-zone-button-active')
				}
			}
		}

		//Instead of using onEvent, use add event listener to avoid overriding previous event handler
		window.addEventListener('resize', debounce(this.onBroadcastWindowResize.bind(this), 250))

		// Event Listener acts as a relay for info to the target zone
		window.addEventListener(
			'targetZoneChange',
			(e: CustomEvent<TargetZoneChangeDetail>) => {
				const sourceRoot = e.detail.sourceRoot
				if (sourceRoot && sourceRoot !== this.getRootNode()) return

				const zoneID = e.detail.zoneID,
					targetZoneID = e.detail.targetZoneID
				let isModal = false

				isSmViewport = isSmallViewport()
				isMdViewport = isMediumViewport()

				if (this.dataZoneId === zoneID) {
					if (TZC.length > 0) {
						for (let i = 0; i < TZC.length; i++) {
							//get container and check id to make sure we're in the right context
							const TZC_UL = TZC[i].parentNode as HTMLElement,
								TZC_UL_Parent = TZC_UL.parentNode as HTMLElement,
								TZC_ZoneID = TZC_UL_Parent.dataset.zoneId

							if (zoneID === TZC_ZoneID && targetZoneID === TZC[i].dataset.zoneSectionId) {
								isModal = false
								if (
									(isSmViewport && TZC[i].dataset.openOnSm === 'inModal') ||
									(isMdViewport && TZC[i].dataset.openOnMd === 'inModal') ||
									(!isSmViewport && !isMdViewport && TZC[i].dataset.openOnLg === 'inModal')
								) {
									const mrkup = TZC[i].innerHTML
									isModal = true

									GBModalService.openModal({
										modalId: TZC[i].dataset.zoneSectionId,
										contentMarkup: mrkup,
										isFullscreen: true,
										isZoneTarget: true,
									})
								}

								if (zoneID === TZC_ZoneID && TZC[i].classList.contains('gb-hide') && !isModal) {
									TZC[i].classList.remove('gb-hide')
								}
							} else {
								if (zoneID === TZC_ZoneID) {
									TZC[i].classList.add('gb-hide')
								}
							}
						}
					} else {
						if (e.detail.targetZoneID === this.dataZoneId) {
							if (this.classList.contains('gb-hide')) {
								this.classList.remove('gb-hide')
							}
						} else {
							if (!this.classList.contains('gb-hide')) {
								this.classList.add('gb-hide')
							}
						}
					}
					for (let i = 0; i < Buttons.length; i++) {
						if (
							Buttons[i].dataset.targetZoneSectionId === e.detail.targetZoneID &&
							Buttons[i].dataset.targetZoneId === e.detail.zoneID
						) {
							if (!Buttons[i].classList.contains('gb-target-zone-button-active')) {
								Buttons[i].classList.add('gb-target-zone-button-active')
							}
						} else if (Buttons[i].dataset.targetZoneId === e.detail.zoneID) {
							if (Buttons[i].classList.contains('gb-target-zone-button-active')) {
								Buttons[i].classList.remove('gb-target-zone-button-active')
							}
						}
					}

					if (this.dataEnableDeepLink && !e.detail.silent) {
						syncUrlFromSelection(this, targetZoneID)
					}
				}
			},
			false
		)

		if (this.dataEnableDeepLink) {
			applyDeepLinkFromUrl(this)

			if (!this.deepLinkNavBound) {
				this.deepLinkNavBound = true
				window.addEventListener('popstate', this.onPopState)
				window.addEventListener('pageshow', this.onPageShow)
			}
		}
	}

	onBroadcastWindowResize(): void {
		const TZC = this.getOwnZoneSections()
		if (TZC.length > 0) {
			// If we have multiple zones, we'll want to use them independently
			const isSmViewport = isSmallViewport()
			const isMdViewport = isMediumViewport()

			for (let i = 0; i < TZC.length; i++) {
				if (
					(isSmViewport && TZC[i].dataset.openOnSm === 'inModal') ||
					(isMdViewport && TZC[i].dataset.openOnMd === 'inModal') ||
					(!isSmViewport && !isMdViewport && TZC[i].dataset.openOnLg === 'inModal')
				) {
					TZC[i].classList.add('gb-hide')
				}
			}
		}
	}

	listenForTime(): void {
		const TZC = this.getOwnZoneSections()

		if (TZC.length > 0) {
			for (let i = 0; i < TZC.length; i++) {
				if (TZC[i].classList.contains('reveal-modal')) {
					TZC[i].classList.remove('reveal-modal')
				}
			}
		}

		// Apply initial visibility for scrubber time 0 (same rules as timeChange)
		this.applyScrubberTimeVisibility(0, TZC)

		// Event Listener acts as a relay for info to the target zone
		window.addEventListener(
			'timeChange',
			(e: CustomEvent) => {
				const currentTime = e.detail.currentTime,
					zoneID = e.detail.zoneID

				if (this.dataZoneId === zoneID) {
					this.applyScrubberTimeVisibility(currentTime, TZC)
				}
			},
			false
		)
	}

	private parseScrubberTime(value: unknown): number {
		const n = parseFloat(String(value))
		return Number.isFinite(n) ? n : NaN
	}

	/**
	 * Equal from/to means always visible. Invalid/missing times also stay visible
	 * so scrubbing never blanks zones that weren't given a real range.
	 */
	private isAlwaysVisibleScrubberRange(min: number, max: number): boolean {
		return !Number.isFinite(min) || !Number.isFinite(max) || min === max
	}

	/**
	 * Show/hide this zone (or its sections) based on scrubber currentTime.
	 * Only runs for data-use-with="scrubber".
	 */
	private applyScrubberTimeVisibility(currentTime: number, TZC: TargetZone[]): void {
		// Ignore bad scrubber payloads — don't hide everything on undefined/NaN time
		if (!Number.isFinite(currentTime)) {
			return
		}

		if (TZC.length > 0) {
			for (let i = 0; i < TZC.length; i++) {
				const TZCmin = this.parseScrubberTime(TZC[i].dataset.timeFrom),
					TZCmax = this.parseScrubberTime(TZC[i].dataset.timeTo)

				if (this.isAlwaysVisibleScrubberRange(TZCmin, TZCmax)) {
					TZC[i].classList.remove('gb-hide')
					continue
				}

				if (currentTime >= TZCmin && currentTime <= TZCmax) {
					TZC[i].classList.remove('gb-hide')
				} else {
					TZC[i].classList.add('gb-hide')
				}
			}
		} else {
			const TZmin = this.parseScrubberTime(this.dataTimeFrom),
				TZmax = this.parseScrubberTime(this.dataTimeTo)

			if (this.isAlwaysVisibleScrubberRange(TZmin, TZmax)) {
				this.classList.remove('gb-hide')
				return
			}

			if (currentTime >= TZmin && currentTime <= TZmax) {
				this.classList.remove('gb-hide')
			} else {
				this.classList.add('gb-hide')
			}
		}
	}
}
